import style from "../../../assets/scss/sub/purchase/orderAndPayment.module.scss";
import {MainWrapper} from "../MainWrapper";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {ArticleTitle} from "../ArticleTitle";
import {PaymentWay} from "../../../types/payment";
import {useAppSelector, useAuth, useLanguage, useLoader, useUserAgent} from "../../../hooks/hooks";
import {orderStatus} from "../../../store/order/orderSlice";
import {cartedProducts} from "../../../store/cart/cartSlice";
import {Price} from "../../../types/cart";
import {PaymentGateway} from "./pg/PaymentGateway";
import {AffiliateCodeVO, DiscountType} from "../../../types/affiliateCode";
import {CouponCodeDTO} from "../../../types/couponCode";
import {AgreementField} from "./hooks/AgreementField";
import {OrderProductsField} from "./hooks/OrderProductsField";
import {useOrderFunctions} from "./hooks/useOrderFunctions";
import {useTranslation} from "react-i18next";
import {CodeDiscountField} from "./hooks/CodeDiscountField";
import {CouponDiscountField} from "./hooks/CouponDiscountField";
import {OrderSummaryField} from "./hooks/OrderSummaryField";
import {PaymentMethodField} from "./hooks/PaymentMethodField";
import {PaymentSection} from "./hooks/PaymentSection";
export function OrderAndPayment() {
    const { t } = useTranslation("order")
    const cartedProductList = useAppSelector(cartedProducts);
    const status = useAppSelector(orderStatus);

    const { setLoaderStatus } = useLoader();
    const { isKorean } = useLanguage();
    const { isLoggedIn } = useAuth();
    const { isMobile } = useUserAgent();

    const [isAgreed, setIsAgreed] = useState(false);
    const [couponDiscountAmount, setCouponDiscountAmount] = useState(0);
    const [selectedCouponIdx, setSelectedCouponIdx] = useState<number | null>(null);
    const [paymentWay, setPaymentWay] = useState<PaymentWay | null>(null);
    const [isClickedPayButton, setIsClickedPayButton] = useState(false);

    const [affiliateCode, setAffiliateCode] = useState<AffiliateCodeVO | null>(null);
    const [couponCode, setCouponCode] = useState<CouponCodeDTO | null>(null);
    const [enteredCode, setEnteredCode] = useState("");
    const [affiliateCodeDiscountAmount, setAffiliateCodeDiscountAmount] = useState(0);
    const [couponCodeDiscountAmount, setCouponCodeDiscountAmount] = useState(0);

    const { getCarts, cartIdxes, onClickTermsLink, currentVoucherType, onClickPayBtn, validateProductLanguage } = useOrderFunctions();
    const isThisCounselorProduct = currentVoucherType ==='counselor'

    const onClickAgreeBtn = useCallback((val: boolean) => setIsAgreed(!val), []);
    const onClickPaymentWayBtn = useCallback((p: PaymentWay) => setPaymentWay(p), []);

    const totalPrice: Price = useMemo(() => {
        if (cartedProductList) {
            return cartedProductList.reduce(
                (acc: Price, curPrd) => {
                    const thisRegularAmount = acc.regularAmount + (curPrd?.totalRegularPrice ?? 0);
                    const thisProductDiscountAmount = acc.productDiscountAmount + (curPrd?.totalDiscountAmount ?? 0);
                    const thisProductAmount = thisRegularAmount - thisProductDiscountAmount;
                    const actualAmount =
                        acc.actualAmount + (curPrd?.totalActualAmount ?? 0) - couponDiscountAmount - affiliateCodeDiscountAmount - couponCodeDiscountAmount;

                    return {
                        regularAmount: thisRegularAmount,
                        productDiscountAmount: thisProductDiscountAmount,
                        productDiscountAmountShown: thisProductDiscountAmount,
                        couponDiscountAmount: couponDiscountAmount,
                        couponDiscountAmountShown:
                            thisProductAmount < couponDiscountAmount ? thisProductAmount : couponDiscountAmount,
                        affiliateCodeDiscountAmount: affiliateCodeDiscountAmount,
                        affiliateCodeDiscountAmountShown:
                            thisProductAmount < affiliateCodeDiscountAmount ? thisProductAmount : affiliateCodeDiscountAmount,
                        actualAmount: actualAmount,
                        actualAmountShown: actualAmount > 0 ? actualAmount : 0,
                        couponCodeDiscountAmount: couponCodeDiscountAmount,
                        couponCodeDiscountAmountShown: thisProductAmount < couponCodeDiscountAmount ? thisProductAmount : couponCodeDiscountAmount,
                    };
                },
                {
                    regularAmount: 0,
                    couponDiscountAmount: couponDiscountAmount,
                    couponDiscountAmountShown: couponDiscountAmount,
                    affiliateCodeDiscountAmount: affiliateCodeDiscountAmount,
                    affiliateCodeDiscountAmountShown: affiliateCodeDiscountAmount,
                    productDiscountAmount: 0,
                    productDiscountAmountShown: 0,
                    actualAmount: 0,
                    actualAmountShown: 0,
                    couponCodeDiscountAmount: couponCodeDiscountAmount,
                    couponCodeDiscountAmountShown: couponCodeDiscountAmount,
                }
            );
        } else {
            return {
                regularAmount: 0,
                productDiscountAmount: 0,
                productDiscountAmountShown: 0,
                couponDiscountAmount: couponDiscountAmount,
                couponDiscountAmountShown: couponDiscountAmount,
                affiliateCodeDiscountAmount: affiliateCodeDiscountAmount,
                affiliateCodeDiscountAmountShown: affiliateCodeDiscountAmount,
                actualAmount: 0,
                actualAmountShown: 0,
                couponCodeDiscountAmount: couponCodeDiscountAmount,
                couponCodeDiscountAmountShown: couponCodeDiscountAmount,
            };
        }
    }, [cartedProductList, couponDiscountAmount, affiliateCodeDiscountAmount, couponCodeDiscountAmount]);
    const [codeDiscountAmount, setCodeDiscountAmount] =
        useState(Math.max(totalPrice.affiliateCodeDiscountAmount, totalPrice.couponCodeDiscountAmount));

    useEffect(() => {
        document.title = `검사이용권 구매 | ${process.env.REACT_APP_TITLE}`;
        window.scroll({ top: 0 });

        if (isLoggedIn) {
            getCarts().then();
        }
        // eslint-disable-next-line
    }, [isLoggedIn]);

    useEffect(() => {
        setLoaderStatus([status]);
        // eslint-disable-next-line
    }, [status]);

    const resetCoupon = useCallback(() => {
        setCouponDiscountAmount(0);
        setSelectedCouponIdx(null);
        setCodeDiscountAmount(0);
    }, []);

    const resetSalesCodeDiscount = useCallback(() => {
        setAffiliateCode(null);
        setCouponCode(null)
        setCodeDiscountAmount(0)
        setEnteredCode("");
    }, []);

    const totalSellingPrice = useMemo(
        () =>
            cartedProductList?.map((it) => it.product.sellingPrice * it.quantity).reduce((total, it) => total + it, 0) ?? 0,
        [cartedProductList]
    );

    // 코드 할인 시 제휴 / 쿠폰코드 공통 초기화 & 값 할당 함수
    const resetAndSetCode = useCallback(
        (params: { type: 'affiliate', code: AffiliateCodeVO } | { type: 'coupon', code: CouponCodeDTO } | null) => {
            if (params) {
                resetCoupon();
                setAffiliateCode(params.type === 'affiliate' ? params.code : null);
                setCouponCode(params.type === 'coupon' ? params.code : null);
            } else {
                setAffiliateCode(null)
                setCouponCode(null)
            }
        },
        // eslint-disable-next-line
        [setAffiliateCode, setCouponCode]
    );

    useEffect(() => {
        if (affiliateCode) {
            switch (affiliateCode.discountType) {
                case DiscountType.AMOUNT:
                    setAffiliateCodeDiscountAmount(affiliateCode.discountValue);
                    break;
                case DiscountType.PERCENT:
                    setAffiliateCodeDiscountAmount(Math.round(totalSellingPrice * (affiliateCode.discountValue / 100)));
                    break;
                default:
                    setAffiliateCodeDiscountAmount(0);
                    break;
            }
        } else {
            setAffiliateCodeDiscountAmount(0);
        }
    }, [affiliateCode, totalSellingPrice]);

    useEffect(() => {
        if (couponCode) {
            switch (couponCode.discountType) {
                case DiscountType.AMOUNT:
                    setCouponCodeDiscountAmount(couponCode.discountAmount);
                    break;
                case DiscountType.PERCENT:
                    setCouponCodeDiscountAmount(Math.round(totalSellingPrice * (couponCode.discountAmount / 100)));
                    break;
                default:
                    setCouponCodeDiscountAmount(0);
                    break;
            }
        } else {
            setCouponCodeDiscountAmount(0);
        }
    }, [couponCode, totalSellingPrice]);

    const totalDiscountAmount = useMemo(
        () =>
            totalPrice.productDiscountAmountShown +
            totalPrice.couponDiscountAmountShown +
            totalPrice.affiliateCodeDiscountAmountShown +
            totalPrice.couponCodeDiscountAmountShown,
        [
            totalPrice.productDiscountAmountShown,
            totalPrice.couponDiscountAmountShown,
            totalPrice.affiliateCodeDiscountAmountShown,
            totalPrice.couponCodeDiscountAmountShown
        ]
    );
    const couponDiscountField = ( // 쿠폰 할인
        <CouponDiscountField totalPrice={totalPrice} selectedCouponIdx={selectedCouponIdx}
                             resetSalesCodeDiscount={resetSalesCodeDiscount} setSelectedCouponIdx={setSelectedCouponIdx}
                             setCouponDiscountAmount={setCouponDiscountAmount} />
    )

    const codeDiscountField = ( // 코드 할인
        <CodeDiscountField
            discountCode={enteredCode}
            setDiscountCode={setEnteredCode}
            setCode={resetAndSetCode}
            cartIdxes={cartIdxes}
            cartedProducts={cartedProductList ?? []}
        />
    );

    const paymentMethodField = ( // 결제 수단
        <PaymentMethodField paymentWay={paymentWay} onClickPaymentWayBtn={onClickPaymentWayBtn} />
    )

    const orderSummaryField = ( // 주문 정보
        <OrderSummaryField totalPrice={totalPrice} totalDiscountAmount={totalDiscountAmount}
                           selectedCouponIdx={selectedCouponIdx} codeDiscountAmount={codeDiscountAmount}
                           isGirdField={isThisCounselorProduct}
        />
    )

    useEffect(() => {
        setCodeDiscountAmount(
            Math.max(
                totalPrice.affiliateCodeDiscountAmount, totalPrice.couponCodeDiscountAmount
            ));
        // eslint-disable-next-line
    }, [totalDiscountAmount]);



    useEffect(() => {
        if (cartedProductList !== undefined) {
            validateProductLanguage(cartedProductList)
        }
        // eslint-disable-next-line
    }, [cartedProductList]);

    return (
        <>
            <MainWrapper showFooter={true} mainClassName={style.orderAndPayment}>
                <>
                    {/* 주문/결제*/}
                    <div className={style.title}>
                        <ArticleTitle title={t("title")} className={style.textArea} />
                    </div>

                    {/* 주문 상품*/}
                    <OrderProductsField />

                    {/* 쿠폰할인 ~ */}
                    <div className={style.orderAndPaymentWrapper}>
                        <PaymentSection
                            codeDiscountField={codeDiscountField}
                            couponDiscountField={couponDiscountField}
                            orderSummaryField={orderSummaryField}
                            paymentMethodField={paymentMethodField}
                            isMobile={isMobile} isCounselor={isThisCounselorProduct}
                            isKorean={isKorean}
                        />
                    </div>

                    {/*  약관 동의  */}
                    <AgreementField isAgreed={isAgreed} onClickAgreeBtn={onClickAgreeBtn} onClickTermsLink={onClickTermsLink} />
                    <button className={style.payButton}
                            onClick={() => onClickPayBtn({isAgreed, paymentWay, pay:setIsClickedPayButton})}
                    >{t("btn.order")}</button>
                </>
            </MainWrapper>
            {paymentWay &&
                <PaymentGateway
                    isClickedPayButton={isClickedPayButton}
                    resetClickedPayButton={() => setIsClickedPayButton(false)}
                    paymentWay={paymentWay}
                    cartIdxes={cartIdxes}
                    price={totalPrice}
                    couponHistoryIdx={selectedCouponIdx}
                    affiliateCodeIdx={affiliateCode?.idx ?? null}
                    couponCodeIdx={couponCode?.couponCodeIdx ?? null}
                />
            }
        </>
    );
}
